import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { dataParaISO, mascaraCPF, mascaraData, validarCPF } from '@/pages/inscricao/utils';
import CampoSenha from '@/pages/entrar/components/CampoSenha';
import type { RespostaTrocarSenha, SessaoNovaSenha } from '../types';

// Alguns erros vêm como código; traduzimos para o que a pessoa entende.
const FRASES_ERRO: Record<string, string> = {
  link_invalido: 'Este link não vale mais.',
  link_expirado: 'Este link venceu. Peça um novo.',
  link_usado: 'Este link já foi usado. Peça um novo.',
  token_invalido: 'Este link não vale mais.',
  expirado: 'Este link venceu. Peça um novo.',
  usado: 'Este link já foi usado. Peça um novo.',
  cpf_errado: 'O CPF não confere com o cadastro.',
  cpf_invalido: 'Confira o CPF: são 11 números.',
  nascimento_errado: 'A data de nascimento não confere com o cadastro.',
  senha_curta: 'A senha precisa ter pelo menos 6 caracteres.',
  senha_fraca: 'Escolha uma senha mais difícil (misture letras e números).',
  bloqueado: 'Muitas tentativas. Peça um novo link para quem te ajudou.',
};

function fraseErro(erro?: string): string {
  if (!erro) return 'Não foi possível agora. Tente de novo em alguns instantes.';
  return FRASES_ERRO[erro] ?? erro;
}

function lerSessao(resposta: RespostaTrocarSenha): SessaoNovaSenha | null {
  if (resposta.sessao?.access_token && resposta.sessao.refresh_token) return resposta.sessao;
  if (resposta.access_token && resposta.refresh_token) {
    return { access_token: resposta.access_token, refresh_token: resposta.refresh_token };
  }
  return null;
}

interface Props {
  token: string;
  pedeCpf: boolean;
  pedeNascimento: boolean;
  aoTrocar: (sessao: SessaoNovaSenha | null, email: string) => void;
}

// Lê a mensagem que a função manda quando o link não vale mais.
async function erroDoInvoke(error: unknown, padrao: string): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const corpo = (await ctx.json()) as Record<string, unknown> | null;
      if (corpo && typeof corpo.erro === 'string' && corpo.erro) return corpo.erro;
      if (corpo && typeof corpo.error === 'string' && corpo.error) return corpo.error;
    } catch {
      // sem corpo legível: usa a mensagem padrão
    }
  }
  return padrao;
}

export default function FormularioNovaSenha({
  token,
  pedeCpf,
  pedeNascimento,
  aoTrocar,
}: Props) {
  const [cpf, setCpf] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [senha, setSenha] = useState('');
  const [senha2, setSenha2] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [restam, setRestam] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function trocar() {
    if (pedeCpf && !validarCPF(cpf)) {
      setErro('Confira o CPF: são 11 números.');
      setRestam(null);
      return;
    }
    if (pedeNascimento && nascimento.replace(/\D/g, '').length !== 8) {
      setErro('Digite a data de nascimento no formato dd/mm/aaaa.');
      setRestam(null);
      return;
    }
    if (senha.length < 6) {
      setErro('A senha precisa ter pelo menos 6 caracteres.');
      setRestam(null);
      return;
    }
    if (senha !== senha2) {
      setErro('As duas senhas não são iguais.');
      setRestam(null);
      return;
    }

    setErro(null);
    setRestam(null);
    setEnviando(true);
    try {
      const { data, error } = await supabase.functions.invoke('nova-senha', {
        body: {
          acao: 'trocar',
          token,
          cpf: pedeCpf ? cpf.replace(/\D/g, '') : undefined,
          nascimento: pedeNascimento ? dataParaISO(nascimento) : undefined,
          senha,
        },
      });

      if (error) {
        const msg = await erroDoInvoke(
          error,
          'Não foi possível agora. Peça um novo link para quem te ajudou.',
        );
        setErro(fraseErro(msg));
        return;
      }

      const resposta = (data ?? {}) as RespostaTrocarSenha;

      if (!resposta.ok) {
        setErro(fraseErro(resposta.erro));
        setRestam(typeof resposta.restam === 'number' ? resposta.restam : null);
        return;
      }

      aoTrocar(lerSessao(resposta), typeof resposta.email === 'string' ? resposta.email : '');
    } catch {
      setErro('Não foi possível agora. Tente de novo em alguns instantes.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void trocar();
      }}
      noValidate
    >
      <div className="space-y-5">
        {pedeCpf && (
          <div>
            <label htmlFor="nova-senha-cpf" className="block text-base font-medium text-foreground-950">
              Seu CPF <span className="text-danger-600">*</span>
            </label>
            <input
              id="nova-senha-cpf"
              name="cpf"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="000.000.000-00"
              value={cpf}
              onChange={(e) => setCpf(mascaraCPF(e.target.value))}
              className="mt-1 w-full border-0 border-b border-secondary-300 bg-transparent px-0 py-2 text-[15px] text-foreground-950 outline-none transition-colors placeholder:text-secondary-400 focus:border-b-2 focus:border-primary-500"
            />
            <p className="mt-1 text-sm text-secondary-500">É o CPF que está no seu cadastro.</p>
          </div>
        )}

        {pedeNascimento && (
          <div>
            <label
              htmlFor="nova-senha-nascimento"
              className="block text-base font-medium text-foreground-950"
            >
              Data de nascimento <span className="text-danger-600">*</span>
            </label>
            <input
              id="nova-senha-nascimento"
              name="nascimento"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="dd/mm/aaaa"
              value={nascimento}
              onChange={(e) => setNascimento(mascaraData(e.target.value))}
              className="mt-1 w-full border-0 border-b border-secondary-300 bg-transparent px-0 py-2 text-[15px] text-foreground-950 outline-none transition-colors placeholder:text-secondary-400 focus:border-b-2 focus:border-primary-500"
            />
          </div>
        )}

        <CampoSenha
          id="nova-senha-1"
          name="senha"
          label="Nova senha"
          help="Pelo menos 6 caracteres."
          value={senha}
          onChange={setSenha}
          required
          autoComplete="new-password"
          mostrarForca
        />

        <CampoSenha
          id="nova-senha-2"
          name="senha2"
          label="Repita a nova senha"
          value={senha2}
          onChange={setSenha2}
          required
          autoComplete="new-password"
        />
      </div>

      {erro && (
        <div className="mt-4 rounded-md border border-danger-300 bg-danger-50 px-3 py-2" role="alert">
          <p className="text-sm text-danger-700">{erro}</p>
          {restam !== null && (
            <p className="mt-1 text-xs text-danger-600">
              {restam === 0
                ? 'Sem tentativas neste link. Peça outro.'
                : `Restam ${restam} tentativa${restam === 1 ? '' : 's'}.`}
            </p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={enviando}
        className="mt-6 flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-[15px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400"
      >
        {enviando ? (
          <>
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-loader-4-line animate-spin" aria-hidden />
            </span>
            Salvando…
          </>
        ) : (
          'Criar nova senha e entrar'
        )}
      </button>
    </form>
  );
}